import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '../../../components/Grid/Grid';
import Button from '../../../components/Button/Button';
import { DialogContext } from '../../../contexts/DialogContext';
import { dialogActions } from '../../../reducers/dialogReducer';
import Dialog from '../../../components/Dialog/Dialog';
import DialogTitle from '../../../components/DialogTitle/DialogTitle';
import useForm from '../../../hooks/useForm';
import DialogContent from '../../../components/DialogContent/DialogContent';
import {
  addControlledFormingElNo,
  getElNoListByProductName,
} from '../../../services/settings-config';
import useCancelToken from '../../../hooks/useCancelToken';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';
import Typography from '../../../components/Typography/Typography';
import useStyles from '../useStyles';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { getProductNameSelections } from '../../../services/general';
import SelectAutoComplete from '../../../components/SelectField/SelectAutoComplete';

const ControlledFormingElNoCreateDialog = ({ isOpen, onClose, onSubmitSuccess }) => {
  const { classes } = useStyles();
  const { newCancelToken } = useCancelToken();
  const initialForm = {
    productName: '',
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const [selectedElNo, setSelectedElNo] = useState([]);
  const [elNoResult, setElNoResult] = useState([]);
  const [elNoResultLoading, setElNoResultLoading] = useState(false);
  const [elNoResultError, setElNoResultError] = useState('');
  const [productSelection, setProductSelection] = useState([]);
  const [statusMsg, setStatusMsg] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const snackbarTimer = useRef(null);
  const { dialogDispatch } = useContext(DialogContext);

  const openSnackbar = () => {
    setShowStatus(true);
    clearTimeout(snackbarTimer.current);
    snackbarTimer.current = setTimeout(() => {
      setShowStatus(false);
      clearTimeout(snackbarTimer.current);
    }, 3000);
  };

  const validationForm = () => {
    if (formData.productName.length === 0) {
      setFormErr({ ...formErr, productName: '請選擇正確的產品名稱' });
      return false;
    }
    return true;
  };

  const handleSearchElNoList = async () => {
    if (!validationForm()) return;
    const cancelToken = newCancelToken();
    await getElNoListByProductName({
      data: {
        productName: formData.productName,
      },
      cancelToken,
      onSuccess: (result) => {
        setElNoResult(result.elNoList);
        setElNoResultError('');
        setElNoResultLoading(false);
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        setElNoResult([]);
        setElNoResultError(noPrivilegeMsg || `查詢產品規格失敗：${result}`);
        setElNoResultLoading(false);
      },
    });
  };

  const handleClose = () => {
    resetFormErr();
    setFormData(initialForm);
    setElNoResult([]);
    setSelectedElNo([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedElNo.length === 0) {
      return;
    }
    const cancelToken = newCancelToken();
    await addControlledFormingElNo({
      data: {
        elNos: selectedElNo,
        productName: formData.productName,
      },
      cancelToken,
      onSuccess: () => {
        resetFormErr();
        handleClose();
        onSubmitSuccess();
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        openSnackbar();
        setStatusMsg(
          noPrivilegeMsg || `新增管制壓型規格設定失敗！請確認網路是否正常或聯繫軟體部門：${result}`
        );
      },
    });
  };

  const handleCancel = () => {
    if (selectedElNo.length > 0) {
      dialogDispatch({
        type: dialogActions.OPEN,
        config: {
          singleBtn: false,
          title: '確認離開？',
          message: '是否確認放棄新增管制壓型規格設定？',
          handleConfirm: handleClose,
        },
      });
    } else {
      handleClose();
    }
  };

  const handleProductSelectionChange = (data) => {
    if (!data) {
      setFormData({
        productName: '',
      });
      return;
    }
    const { value } = data;
    setFormData({
      productName: value,
    });
  };

  const handleCheckedChange = ({ targetElNo, checked }) => {
    if (checked) {
      setSelectedElNo((prev) => [...prev, targetElNo]);
    } else {
      setSelectedElNo((prev) => prev.filter((elNo) => elNo !== targetElNo));
    }
  };

  const handleGetProductNameSelections = async () => {
    const cancelToken = newCancelToken();
    await getProductNameSelections({
      cancelToken,
      onSuccess: (result) => {
        setProductSelection(
          result.prodNameList.map((item) => ({
            name: item,
            value: item,
          }))
        );
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        dialogDispatch({
          type: dialogActions.OPEN,
          config: {
            title: '無法取得產品選項',
            message:
              noPrivilegeMsg || `目前無法取得產品選項，請確認網路是否正常或聯繫軟體部門：${result}`,
          },
        });
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      handleGetProductNameSelections();
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={(_e, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="create-powder-batch-temperature-title">新增管制壓型規格</DialogTitle>
      <DialogContent dividers sx={{ paddingTop: 3, paddingBottom: 3 }}>
        <Grid container direction="column" spacing={2}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={6}>
              <SelectAutoComplete
                label="產品名稱"
                name="productName"
                placeholder="請選擇產品"
                value={formData.productName}
                onFocus={handleProductSelectionChange}
                handleChange={handleProductSelectionChange}
                selections={productSelection}
                id="productName"
                errormsg="請選擇產品"
              />
            </Grid>
            <Grid item xs={6} container gap={1} alignItems="center" sx={{ marginTop: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleSearchElNoList}
              >
                查詢
              </Button>
              <Button variant="outlined" color="grey" size="large" onClick={handleCancel}>
                取消
              </Button>
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction="column"
            xs={12}
            sx={
              elNoResultLoading || elNoResult?.length > 0 ? { paddingTop: 3, paddingBottom: 3 } : {}
            }
          >
            {elNoResultLoading && <CircularProgress />}
            {!elNoResultLoading && elNoResultError && (
              <Typography variant="body1" color="error" textAlign="center" sx={{ marginBottom: 3 }}>
                {`查詢產品規格失敗：${elNoResultError}`}
              </Typography>
            )}
            <Snackbar msg={statusMsg} type="error" open={showStatus} className={classes.snackbar} />
            {!elNoResultLoading && elNoResult?.length > 0 && (
              <Grid
                container
                item
                xs={12}
                spacing={2}
                direction="column"
                sx={{
                  paddingLeft: 2,
                  paddingRight: 2,
                  overflowY: 'scroll',
                  // height: '400px',
                  flexWrap: 'nowrap',
                }}
              >
                <Typography variant="body1" fontWeight="bold" sx={{ marginBottom: 2 }}>
                  產品規格查詢結果
                </Typography>
                <Grid
                  container
                  item
                  xs={12}
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    padding: 0,
                    marginBottom: 0.6,
                  })}
                >
                  <Grid item xs={1}>
                    勾選
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body1">料號</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body1">材質</Typography>
                  </Grid>
                </Grid>
                {elNoResult.map((item) => (
                  <Grid
                    container
                    item
                    xs={12}
                    key={item.elNo}
                    sx={(theme) => ({
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      padding: 0,
                      marginBottom: 0.6,
                    })}
                  >
                    <Grid item xs={1}>
                      <Checkbox
                        checked={item.controlled || selectedElNo.includes(item.elNo)}
                        handleChange={(checked) =>
                          handleCheckedChange({
                            targetElNo: item.elNo,
                            checked,
                          })
                        }
                        disabled={item.controlled}
                      />
                    </Grid>
                    <Grid item xs={7} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1">{item.elNo}</Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1">{item.materialName}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Grid container item xs={12} justifyContent="flex-end" sx={{ padding: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={selectedElNo?.length === 0}
        >
          確認新增
        </Button>
      </Grid>
    </Dialog>
  );
}; // NewQCTypeAction
ControlledFormingElNoCreateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
};

export default ControlledFormingElNoCreateDialog;
